import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../../../context/auth-context";

import {
  adicionarCoordenadorEmpresa,
  fetchCoordenadores,
  formatCNPJ,
  removerCoordenadorEmpresa,
  type ClienteAPI,
  type CoordenadorAPI,
} from "./empresasCadastradasData";

type EmpresaClienteSidePanelProps = {
  open: boolean;
  cliente: ClienteAPI | null;
  onClose: () => void;
  refresh?: () => Promise<void> | void;
};

type BusyAction = "adicionar" | `remover-${number}` | null;

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const CSS = `
.empresa-cliente-backdrop {
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  left: ${LEFTBAR_WIDTH}px;
  right: 0;
  bottom: 0;
  z-index: 1190;

  display: flex;
  justify-content: flex-end;

  background: rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);

  box-sizing: border-box;
}

.empresa-cliente-panel {
  width: min(
    760px,
    calc(100vw - ${LEFTBAR_WIDTH}px)
  );

  height: calc(
    100vh - ${HEADER_HEIGHT}px
  );

  display: flex;
  flex-direction: column;

  background: #ffffff;
  border-left: 1px solid #d9e2ec;

  box-shadow:
    -18px 0 46px
    rgba(15, 23, 42, 0.22);

  color: #0f172a;

  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  box-sizing: border-box;
}

.empresa-cliente-header {
  padding: 18px 20px 16px;
  border-bottom: 1px solid #e5eaf0;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 16px;
}

.empresa-cliente-eyebrow {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.42px;
  text-transform: uppercase;
}

.empresa-cliente-title {
  margin: 6px 0 4px;

  color: #0f172a;
  font-size: 21px;
  font-weight: 950;
  line-height: 1.18;

  overflow-wrap: anywhere;
}

.empresa-cliente-subtitle {
  margin: 0;

  color: #64748b;
  font-size: 12.5px;
  font-weight: 760;
  line-height: 1.45;
}

.empresa-cliente-close-button {
  width: 36px;
  height: 36px;
  flex-shrink: 0;

  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;

  font-size: 24px;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empresa-cliente-content {
  flex: 1;
  min-height: 0;

  overflow-y: auto;
  padding: 14px;

  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #f1f5f9 100%
    );

  display: flex;
  flex-direction: column;

  gap: 12px;

  box-sizing: border-box;
}

.empresa-cliente-section,
.empresa-cliente-summary-card {
  border: 1px solid #e2e8f0;
  background: #ffffff;

  padding: 14px;

  box-sizing: border-box;
}

.empresa-cliente-section {
  display: flex;
  flex-direction: column;

  gap: 12px;
}

.empresa-cliente-section-title strong {
  display: block;

  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.empresa-cliente-section-title span {
  display: block;
  margin-top: 3px;

  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.empresa-cliente-summary-card span {
  display: block;

  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.empresa-cliente-summary-card strong {
  display: block;
  margin-top: 4px;

  color: #0f172a;
  font-size: 18px;
  font-weight: 950;
}

.empresa-cliente-info-grid {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap: 8px;
}

.empresa-cliente-info-box {
  min-width: 0;

  border: 1px solid #edf2f7;
  background: #f8fafc;

  padding: 10px;

  display: flex;
  flex-direction: column;

  gap: 4px;
}

.empresa-cliente-info-box.is-wide {
  grid-column: span 2;
}

.empresa-cliente-info-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.empresa-cliente-info-value {
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;

  overflow-wrap: anywhere;
}

.empresa-cliente-coordinator-list {
  display: flex;
  flex-direction: column;

  gap: 8px;
}

.empresa-cliente-coordinator-card {
  border: 1px solid #e2e8f0;
  background: #f8fafc;

  padding: 11px 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;
}

.empresa-cliente-coordinator-data {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}

.empresa-cliente-coordinator-name {
  color: #0f172a;
  font-size: 13px;
  font-weight: 950;

  overflow-wrap: anywhere;
}

.empresa-cliente-coordinator-meta {
  color: #64748b;
  font-size: 11.5px;
  font-weight: 760;

  overflow-wrap: anywhere;
}

.empresa-cliente-add-grid {
  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    auto;

  gap: 8px;
  align-items: end;
}

.empresa-cliente-field {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 6px;
}

.empresa-cliente-field label {
  color: #334155;
  font-size: 11.5px;
  font-weight: 900;
}

.empresa-cliente-field select {
  width: 100%;
  height: 38px;

  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;

  padding: 0 10px;

  font: inherit;
  font-size: 13px;
  font-weight: 760;

  box-sizing: border-box;
}

.empresa-cliente-primary-button,
.empresa-cliente-danger-button {
  min-height: 38px;

  padding: 0 13px;

  font: inherit;
  font-size: 12px;
  font-weight: 900;

  cursor: pointer;
}

.empresa-cliente-primary-button {
  border: 1px solid #2f3f59;
  background: #2f3f59;
  color: #ffffff;
}

.empresa-cliente-danger-button {
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #991b1b;
}

.empresa-cliente-primary-button:disabled,
.empresa-cliente-danger-button:disabled,
.empresa-cliente-close-button:disabled,
.empresa-cliente-field select:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.empresa-cliente-alert,
.empresa-cliente-empty,
.empresa-cliente-loading {
  border: 1px solid #dbe4ef;
  background: #ffffff;

  padding: 12px;

  color: #475569;
  font-size: 12.5px;
  font-weight: 850;
}

.empresa-cliente-alert.is-error {
  border-color: #fecaca;
  background: #fff5f5;
  color: #991b1b;
}

.empresa-cliente-alert.is-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.empresa-cliente-auth-info {
  border: 1px solid #dbe4ef;
  background: #f8fafc;

  padding: 10px;

  color: #475569;
  font-size: 11.5px;
  font-weight: 760;
}

@media (
  max-width:
  ${MOBILE_BREAKPOINT}px
) {
  .empresa-cliente-backdrop {
    top: 0;
    left: 0;
    z-index: 1400;
  }

  .empresa-cliente-panel {
    width: 100vw;
    height: 100vh;
  }
}

@media (max-width: 640px) {
  .empresa-cliente-info-grid,
  .empresa-cliente-add-grid {
    grid-template-columns: 1fr;
  }

  .empresa-cliente-info-box.is-wide {
    grid-column: span 1;
  }

  .empresa-cliente-primary-button,
  .empresa-cliente-danger-button {
    width: 100%;
  }

  .empresa-cliente-coordinator-card {
    flex-direction: column;
    align-items: stretch;
  }
}
`;

function normalizeName(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLocaleUpperCase("pt-BR")
    .replace(/\s+/g, " ");
}

function parsePositiveInteger(value: unknown): number | null {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getEmpresaClienteId(cliente: ClienteAPI | null): number | null {
  if (!cliente) {
    return null;
  }

  return (
    parsePositiveInteger(cliente.id) ??
    parsePositiveInteger(cliente.empresa_cliente_id)
  );
}

function getEmpresaAtivaId(
  sessao: {
    empresa_ativa_id?: string | number | null;

    empresa_id?: string | number | null;

    empresa?: {
      id?: string | number | null;
    } | null;
  } | null
): number | null {
  if (!sessao) {
    return null;
  }

  return parsePositiveInteger(
    sessao.empresa_ativa_id ?? sessao.empresa_id ?? sessao.empresa?.id
  );
}

function deduplicateCoordenadores(
  coordenadores: CoordenadorAPI[]
): CoordenadorAPI[] {
  const result: CoordenadorAPI[] = [];

  for (const coordenador of coordenadores) {
    const id = parsePositiveInteger(coordenador.id);

    const nome = String(coordenador.nome ?? "").trim();

    if (!nome && id === null) {
      continue;
    }

    const index = result.findIndex((item) => {
      const itemId = parsePositiveInteger(item.id);

      if (id !== null && itemId === id) {
        return true;
      }

      return Boolean(nome && normalizeName(item.nome) === normalizeName(nome));
    });

    if (index === -1) {
      result.push({
        ...coordenador,

        id,

        nome: nome || `Coordenador ${id}`,
      });

      continue;
    }

    const existing = result[index];

    result[index] = {
      id: existing.id ?? id,

      nome:
        existing.nome && !/^Coordenador \d+$/.test(existing.nome)
          ? existing.nome
          : nome || existing.nome,

      email: existing.email ?? coordenador.email ?? null,

      telefone: existing.telefone ?? coordenador.telefone ?? null,
    };
  }

  return result.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function getResponsaveisCliente(cliente: ClienteAPI): CoordenadorAPI[] {
  const responsaveis = Array.isArray(cliente.responsaveis)
    ? cliente.responsaveis
    : [];

  const result: CoordenadorAPI[] = responsaveis.map((responsavel) => ({
    id: parsePositiveInteger(responsavel.id),

    nome: String(responsavel.nome ?? "").trim(),

    email: String(responsavel.email ?? "").trim() || null,

    telefone: String(responsavel.telefone ?? "").trim() || null,
  }));

  const ids = Array.isArray(cliente.colaborador_interno_responsavel)
    ? cliente.colaborador_interno_responsavel
    : [];

  for (const rawId of ids) {
    const id = parsePositiveInteger(rawId);

    if (!id) {
      continue;
    }

    const exists = result.some((item) => parsePositiveInteger(item.id) === id);

    if (!exists) {
      result.push({
        id,

        nome: `Coordenador ${id}`,

        email: null,
        telefone: null,
      });
    }
  }

  return deduplicateCoordenadores(result);
}

function enrichResponsaveis(
  responsaveis: CoordenadorAPI[],

  catalogo: CoordenadorAPI[]
): CoordenadorAPI[] {
  return deduplicateCoordenadores(
    responsaveis.map((responsavel) => {
      const id = parsePositiveInteger(responsavel.id);

      if (id) {
        const byId = catalogo.find(
          (coordenador) => parsePositiveInteger(coordenador.id) === id
        );

        if (byId) {
          return {
            id,

            nome: byId.nome || responsavel.nome,

            email: responsavel.email ?? byId.email ?? null,

            telefone: responsavel.telefone ?? byId.telefone ?? null,
          };
        }
      }

      const matchesByName = catalogo.filter(
        (coordenador) =>
          normalizeName(coordenador.nome) === normalizeName(responsavel.nome)
      );

      if (matchesByName.length === 1) {
        const match = matchesByName[0];

        return {
          id: parsePositiveInteger(match.id),

          nome: match.nome,

          email: responsavel.email ?? match.email ?? null,

          telefone: responsavel.telefone ?? match.telefone ?? null,
        };
      }

      return responsavel;
    })
  );
}

const InfoBox: React.FC<{
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}> = ({ label, value, wide = false }) => (
  <div className={`empresa-cliente-info-box${wide ? " is-wide" : ""}`}>
    <span className="empresa-cliente-info-label">{label}</span>

    <strong className="empresa-cliente-info-value">{value || "—"}</strong>
  </div>
);

const EmpresaClienteSidePanel: React.FC<EmpresaClienteSidePanelProps> = ({
  open,
  cliente,
  onClose,
  refresh,
}) => {
  const { apiBase, sessao, loading: authLoading } = useAuth();

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [responsaveisAtuais, setResponsaveisAtuais] = useState<
    CoordenadorAPI[]
  >([]);

  const [todosCoordenadores, setTodosCoordenadores] = useState<
    CoordenadorAPI[]
  >([]);

  const [coordenadorSelecionado, setCoordenadorSelecionado] = useState("");

  const [loadingCoordenadores, setLoadingCoordenadores] = useState(false);

  const [busyAction, setBusyAction] = useState<BusyAction>(null);

  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);

  const busy = busyAction !== null;

  const empresaClienteId = useMemo(
    () => getEmpresaClienteId(cliente),
    [cliente]
  );

  const empresaAtivaId = useMemo(() => getEmpresaAtivaId(sessao), [sessao]);

  const coordenadoresDisponiveis = useMemo(() => {
    const idsVinculados = new Set(
      responsaveisAtuais
        .map((responsavel) => parsePositiveInteger(responsavel.id))
        .filter((id): id is number => id !== null)
    );

    const nomesVinculados = new Set(
      responsaveisAtuais.map((responsavel) => normalizeName(responsavel.nome))
    );

    return todosCoordenadores
      .filter((coordenador) => {
        const id = parsePositiveInteger(coordenador.id);

        if (id === null) {
          return false;
        }

        if (idsVinculados.has(id)) {
          return false;
        }

        return !nomesVinculados.has(normalizeName(coordenador.nome));
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [todosCoordenadores, responsaveisAtuais]);

  useEffect(() => {
    if (!open || !cliente) {
      return;
    }

    setResponsaveisAtuais(getResponsaveisCliente(cliente));
  }, [open, cliente]);

  useEffect(() => {
    if (!open || !cliente) {
      return;
    }

    let cancelled = false;

    setCoordenadorSelecionado("");

    setFeedbackError(null);
    setFeedbackSuccess(null);

    setLoadingCoordenadores(true);

    fetchCoordenadores()
      .then((coordenadores) => {
        if (cancelled) {
          return;
        }

        const catalogo = deduplicateCoordenadores(coordenadores);

        setTodosCoordenadores(catalogo);

        setResponsaveisAtuais((current) =>
          enrichResponsaveis(current, catalogo)
        );
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setFeedbackError(
          error instanceof Error
            ? error.message
            : "Não foi possível carregar os coordenadores."
        );

        setTodosCoordenadores([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCoordenadores(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, cliente?.id, cliente?.empresa_cliente_id, cliente?.cnpj]);

  useEffect(() => {
    if (!open || todosCoordenadores.length === 0) {
      return;
    }

    setResponsaveisAtuais((current) =>
      enrichResponsaveis(current, todosCoordenadores)
    );
  }, [open, todosCoordenadores]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open || !cliente) {
    return null;
  }

  const nomeEmpresa =
    String(cliente.nome_fantasia || cliente.razao_social || "").trim() ||
    "Empresa sem nome";

  async function handleAdicionar() {
    setFeedbackError(null);
    setFeedbackSuccess(null);

    if (authLoading) {
      setFeedbackError("A sessão do usuário ainda está sendo carregada.");

      return;
    }

    if (!apiBase) {
      setFeedbackError("A base autenticada da API não foi informada.");

      return;
    }

    if (!empresaClienteId) {
      setFeedbackError("A listagem não informou o ID da empresa cliente.");

      return;
    }

    const coordenadorId = parsePositiveInteger(coordenadorSelecionado);

    if (!coordenadorId) {
      setFeedbackError("Selecione um coordenador válido.");

      return;
    }

    const coordenador = todosCoordenadores.find(
      (item) => parsePositiveInteger(item.id) === coordenadorId
    );

    if (!coordenador) {
      setFeedbackError("O coordenador selecionado não foi encontrado.");

      return;
    }

    try {
      setBusyAction("adicionar");

      await adicionarCoordenadorEmpresa(
        apiBase,
        empresaClienteId,
        coordenadorId
      );

      setResponsaveisAtuais((current) =>
        deduplicateCoordenadores([...current, coordenador])
      );

      setCoordenadorSelecionado("");

      setFeedbackSuccess(
        `${coordenador.nome} foi adicionado como coordenador responsável.`
      );

      await refresh?.();
    } catch (error: unknown) {
      setFeedbackError(
        error instanceof Error
          ? error.message
          : "Não foi possível adicionar o coordenador."
      );
    } finally {
      setBusyAction(null);
    }
  }

  async function handleRemover(coordenador: CoordenadorAPI) {
    setFeedbackError(null);
    setFeedbackSuccess(null);

    if (authLoading) {
      setFeedbackError("A sessão do usuário ainda está sendo carregada.");

      return;
    }

    if (!apiBase) {
      setFeedbackError("A base autenticada da API não foi informada.");

      return;
    }

    if (!empresaClienteId) {
      setFeedbackError("A listagem não informou o ID da empresa cliente.");

      return;
    }

    const coordenadorId = parsePositiveInteger(coordenador.id);

    if (!coordenadorId) {
      setFeedbackError(
        `Não é possível remover ${coordenador.nome}, pois não foi possível identificar seu ID.`
      );

      return;
    }

    if (responsaveisAtuais.length <= 1) {
      setFeedbackError(
        "A empresa precisa permanecer vinculada a pelo menos um coordenador."
      );

      return;
    }

    const confirmado = window.confirm(
      `Deseja remover ${coordenador.nome} dos coordenadores responsáveis por ${nomeEmpresa}?`
    );

    if (!confirmado) {
      return;
    }

    try {
      setBusyAction(`remover-${coordenadorId}`);

      await removerCoordenadorEmpresa(apiBase, empresaClienteId, coordenadorId);

      setResponsaveisAtuais((current) =>
        current.filter(
          (item) => parsePositiveInteger(item.id) !== coordenadorId
        )
      );

      setFeedbackSuccess(
        `${coordenador.nome} foi removido dos coordenadores responsáveis.`
      );

      await refresh?.();
    } catch (error: unknown) {
      setFeedbackError(
        error instanceof Error
          ? error.message
          : "Não foi possível remover o coordenador."
      );
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <>
      <style>{CSS}</style>

      <div
        className="empresa-cliente-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !busy) {
            onClose();
          }
        }}
      >
        <aside
          className="empresa-cliente-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Detalhamento da empresa cliente"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="empresa-cliente-header">
            <div>
              <div className="empresa-cliente-eyebrow">
                Coordenação / Empresas e clientes
              </div>

              <h2 className="empresa-cliente-title">{nomeEmpresa}</h2>

              <p className="empresa-cliente-subtitle">
                Consulta dos dados cadastrais e gerenciamento dos coordenadores
                responsáveis por esta empresa.
              </p>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              className="empresa-cliente-close-button"
              onClick={onClose}
              disabled={busy}
              aria-label="Fechar detalhamento da empresa"
            >
              ×
            </button>
          </header>

          <div className="empresa-cliente-content">
            <div className="empresa-cliente-summary-card">
              <span>Coordenadores vinculados</span>

              <strong>{responsaveisAtuais.length}</strong>
            </div>

            <div className="empresa-cliente-auth-info">
              Empresa ativa da sessão:{" "}
              <strong>{empresaAtivaId ?? "não identificada"}</strong>
            </div>

            <section className="empresa-cliente-section">
              <div className="empresa-cliente-section-title">
                <strong>Informações da empresa</strong>

                <span>Dados cadastrais do cliente selecionado.</span>
              </div>

              <div className="empresa-cliente-info-grid">
                <InfoBox label="ID" value={empresaClienteId || "—"} />

                <InfoBox label="CNPJ" value={formatCNPJ(cliente.cnpj)} />

                <InfoBox
                  label="Nome fantasia"
                  value={cliente.nome_fantasia || "—"}
                />

                <InfoBox
                  wide
                  label="Razão social"
                  value={cliente.razao_social || "—"}
                />

                {cliente.email && (
                  <InfoBox label="E-mail" value={cliente.email} />
                )}

                {cliente.telefone && (
                  <InfoBox label="Telefone" value={cliente.telefone} />
                )}
              </div>
            </section>

            <section className="empresa-cliente-section">
              <div className="empresa-cliente-section-title">
                <strong>Coordenadores responsáveis</strong>

                <span>Coordenadores atualmente vinculados à empresa.</span>
              </div>

              {responsaveisAtuais.length === 0 ? (
                <div className="empresa-cliente-empty">
                  Nenhum coordenador está vinculado a esta empresa.
                </div>
              ) : (
                <div className="empresa-cliente-coordinator-list">
                  {responsaveisAtuais.map((coordenador, index) => {
                    const coordenadorId = parsePositiveInteger(coordenador.id);

                    const removendo =
                      coordenadorId !== null &&
                      busyAction === `remover-${coordenadorId}`;

                    return (
                      <div
                        key={
                          coordenadorId
                            ? `coordenador-${coordenadorId}`
                            : `coordenador-sem-id-${normalizeName(
                                coordenador.nome
                              )}-${index}`
                        }
                        className="empresa-cliente-coordinator-card"
                      >
                        <div className="empresa-cliente-coordinator-data">
                          <strong className="empresa-cliente-coordinator-name">
                            {coordenador.nome}
                          </strong>

                          <span className="empresa-cliente-coordinator-meta">
                            {coordenador.email ||
                              (coordenadorId
                                ? `ID ${coordenadorId}`
                                : "ID não identificado")}
                          </span>
                        </div>

                        <button
                          type="button"
                          className="empresa-cliente-danger-button"
                          disabled={
                            busy ||
                            !apiBase ||
                            !empresaClienteId ||
                            !coordenadorId ||
                            responsaveisAtuais.length <= 1
                          }
                          title={
                            responsaveisAtuais.length <= 1
                              ? "A empresa precisa manter pelo menos um coordenador."
                              : !coordenadorId
                              ? "Não foi possível identificar o ID deste coordenador."
                              : "Remover coordenador da empresa"
                          }
                          onClick={() => handleRemover(coordenador)}
                        >
                          {removendo ? "Removendo..." : "Remover"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="empresa-cliente-section">
              <div className="empresa-cliente-section-title">
                <strong>Adicionar coordenador</strong>

                <span>
                  Selecione um coordenador ainda não vinculado a esta empresa.
                </span>
              </div>

              {loadingCoordenadores ? (
                <div className="empresa-cliente-loading">
                  Carregando coordenadores disponíveis...
                </div>
              ) : (
                <div className="empresa-cliente-add-grid">
                  <div className="empresa-cliente-field">
                    <label htmlFor="empresa-cliente-coordenador">
                      Coordenador
                    </label>

                    <select
                      id="empresa-cliente-coordenador"
                      value={coordenadorSelecionado}
                      disabled={
                        busy ||
                        !apiBase ||
                        !empresaClienteId ||
                        coordenadoresDisponiveis.length === 0
                      }
                      onChange={(event) => {
                        setCoordenadorSelecionado(event.target.value);

                        setFeedbackError(null);

                        setFeedbackSuccess(null);
                      }}
                    >
                      <option value="">
                        {coordenadoresDisponiveis.length > 0
                          ? "Selecione um coordenador"
                          : "Nenhum coordenador disponível"}
                      </option>

                      {coordenadoresDisponiveis.map((coordenador) => (
                        <option
                          key={String(coordenador.id)}
                          value={String(coordenador.id)}
                        >
                          {coordenador.nome}

                          {coordenador.email ? ` — ${coordenador.email}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="empresa-cliente-primary-button"
                    disabled={
                      busy ||
                      !apiBase ||
                      !empresaClienteId ||
                      !coordenadorSelecionado
                    }
                    onClick={handleAdicionar}
                  >
                    {busyAction === "adicionar"
                      ? "Adicionando..."
                      : "Adicionar"}
                  </button>
                </div>
              )}
            </section>

            {!empresaClienteId && (
              <div className="empresa-cliente-alert is-error">
                A listagem não forneceu o ID da empresa cliente.
              </div>
            )}

            {!authLoading && !apiBase && (
              <div className="empresa-cliente-alert is-error">
                O contexto de autenticação não informou a base da API.
              </div>
            )}

            {feedbackError && (
              <div className="empresa-cliente-alert is-error">
                {feedbackError}
              </div>
            )}

            {feedbackSuccess && (
              <div className="empresa-cliente-alert is-success">
                {feedbackSuccess}
              </div>
            )}
          </div>
        </aside>
      </div>
    </>
  );
};

export default EmpresaClienteSidePanel;
